/* ═══════════════════════════════════════════════════════════
   RSV Project Modal — controller + tracking
   ═══════════════════════════════════════════════════════════
   Depends on:
     - <dialog id="rsv-project-modal"> rendered by rsv-project-modal.liquid
     - <script id="rsv-project-data" type="application/json"> populated by rsv-project-data.liquid
     - window.RSV_PROFILE (from rsv-profile.liquid) — optional but used when present

   Public API:
     window.RSVProjectModal.open(slug)
     window.RSVProjectModal.close()
     window.RSVProjectModal.swap(slug)

   Events tracked (forwarded to PostHog + dataLayer via RSV_PROFILE.track):
     project_tile_impression, project_tile_clicked,
     project_modal_opened, project_modal_closed,
     project_modal_photo_viewed, project_modal_stone_clicked,
     project_modal_sample_cta, project_modal_related_swap,
     project_filter_applied
   ═══════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  var DATA_ATTR = 'rsv-project-data';
  var MODAL_ID = 'rsv-project-modal';

  var projects = null; // loaded once from JSON island
  var modal = null;
  var state = {
    currentSlug: null,
    currentIndex: 0,
    openAt: 0
  };

  /* ── Track helper: fallback to direct posthog/dataLayer if RSV_PROFILE missing ── */
  function track(evt, detail) {
    detail = detail || {};
    try {
      if (window.RSV_PROFILE && typeof window.RSV_PROFILE.track === 'function') {
        window.RSV_PROFILE.track(evt, detail);
        return;
      }
    } catch (e) {}
    try { if (window.posthog) window.posthog.capture(evt, detail); } catch (e) {}
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'dl_' + evt, rsv: detail });
    } catch (e) {}
  }

  function affinity(field, key, weight) {
    try {
      if (window.RSV_PROFILE && typeof window.RSV_PROFILE.affinity === 'function') {
        window.RSV_PROFILE.affinity(field, key, weight);
      }
    } catch (e) {}
  }

  /* ── Load project data from JSON island ── */
  function loadProjects() {
    if (projects) return projects;
    var el = document.getElementById(DATA_ATTR);
    if (!el) {
      console.warn('[rsv-project-modal] no #' + DATA_ATTR + ' island found');
      return [];
    }
    try {
      projects = JSON.parse(el.textContent || '[]');
    } catch (e) {
      console.error('[rsv-project-modal] failed to parse project data', e);
      projects = [];
    }
    return projects;
  }

  function findProject(slug) {
    var list = loadProjects();
    for (var i = 0; i < list.length; i++) {
      if (list[i].slug === slug) return list[i];
    }
    return null;
  }

  /* ── Render modal content ── */
  function renderModal(p) {
    if (!modal) return;

    var title = modal.querySelector('[data-title]');
    var loc = modal.querySelector('[data-loc]');
    var story = modal.querySelector('[data-story]');
    var slides = modal.querySelector('[data-slides]');
    var stones = modal.querySelector('[data-stones]');
    var relatedWrap = modal.querySelector('[data-related-wrap]');
    var related = modal.querySelector('[data-related]');
    var sampleCta = modal.querySelector('[data-sample-cta]');

    title.textContent = p.title || '';
    loc.textContent = p.location || '';

    // Story (prefer rich HTML)
    story.innerHTML = p.story_long_html || p.story_long || '';

    // Pills
    setPill(modal.querySelector('[data-pill-type]'), p.project_type && p.project_type.replace(/_/g, ' '));
    setPill(modal.querySelector('[data-pill-sqft]'), p.sqft ? p.sqft + ' sqft' : '');
    setPill(modal.querySelector('[data-pill-cost]'), p.cost_display || '');
    setPill(modal.querySelector('[data-pill-weeks]'), p.build_weeks ? p.build_weeks + ' wk build' : '');

    // Photos: hero + gallery (deduped)
    var photos = [];
    if (p.hero_photo) photos.push({ src: p.hero_photo, caption: '' });
    (p.gallery_photos || []).forEach(function (src, i) {
      if (src && photos.indexOf(src) === -1) {
        photos.push({ src: src, caption: (p.photo_captions && p.photo_captions[i]) || '' });
      }
    });
    slides.innerHTML = '';
    photos.forEach(function (ph) {
      var div = document.createElement('div');
      div.className = 'rsv-pm__slide';
      div.style.backgroundImage = 'url("' + ph.src + '")';
      div.setAttribute('data-caption', ph.caption);
      slides.appendChild(div);
    });
    state.currentIndex = 0;
    updateCarousel();

    // Stones used
    stones.innerHTML = '';
    if (!p.stones_used || !p.stones_used.length) {
      stones.parentElement.style.display = 'none';
    } else {
      stones.parentElement.style.display = '';
      p.stones_used.forEach(function (s) {
        var a = document.createElement('a');
        a.className = 'rsv-pm__stone';
        a.href = s.url || ('/products/' + s.handle);
        a.setAttribute('data-stone-handle', s.handle);
        a.innerHTML =
          '<div class="rsv-pm__stone-img" style="background-image:url(\'' + (s.image || '') + '\')"></div>' +
          '<div class="rsv-pm__stone-meta">' +
          '<h4 class="rsv-pm__stone-title">' + escapeHtml(s.title || '') + '</h4>' +
          '<div class="rsv-pm__stone-price">' + escapeHtml(s.price || '') + '</div>' +
          '</div>';
        a.addEventListener('click', function () {
          track('project_modal_stone_clicked', {
            project_slug: p.slug, stone_handle: s.handle, stone_title: s.title
          });
          affinity('style_affinity', p.style_label, 0.5);
          affinity('color_affinity', p.color_label, 0.5);
        });
        stones.appendChild(a);
      });
    }

    // Sample CTA — build a cart permalink of sample SKUs for the stones used
    if (sampleCta) {
      var sampleHandles = (p.stones_used || [])
        .map(function (s) { return 'sample-' + (s.handle || ''); })
        .filter(function (h) { return h.length > 'sample-'.length; });
      if (sampleHandles.length) {
        sampleCta.href = '/cart/add?' + sampleHandles
          .map(function (h) { return 'items[][id]=' + encodeURIComponent(h); })
          .join('&') + '&return_to=/pages/stone-sample-kit';
      } else {
        sampleCta.href = '/pages/stone-sample-kit';
      }
      sampleCta.onclick = function () {
        track('project_modal_sample_cta', { project_slug: p.slug, stones: sampleHandles });
        try {
          if (window.RSV_PROFILE) window.RSV_PROFILE.set('sample_intent', true, 'high');
        } catch (e) {}
      };
    }

    // Related projects (same style_label OR color_label, excluding self)
    var list = loadProjects();
    var matches = list.filter(function (q) {
      if (q.slug === p.slug) return false;
      return q.style_label === p.style_label || q.color_label === p.color_label;
    }).slice(0, 3);
    if (matches.length) {
      relatedWrap.hidden = false;
      related.innerHTML = '';
      matches.forEach(function (q) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'rsv-pm__related-tile';
        btn.style.backgroundImage = 'url("' + (q.hero_photo || '') + '")';
        btn.innerHTML = '<span>' + escapeHtml(q.title || '') + '</span>';
        btn.addEventListener('click', function () {
          track('project_modal_related_swap', {
            from_slug: p.slug, to_slug: q.slug
          });
          window.RSVProjectModal.swap(q.slug);
        });
        related.appendChild(btn);
      });
    } else {
      relatedWrap.hidden = true;
    }
  }

  function setPill(el, text) {
    if (!el) return;
    text = (text || '').toString().trim();
    if (text) { el.textContent = text; el.style.display = ''; }
    else { el.textContent = ''; el.style.display = 'none'; }
  }

  function escapeHtml(s) {
    return (s || '').replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ── Carousel ── */
  function updateCarousel() {
    var slides = modal.querySelector('[data-slides]');
    var counter = modal.querySelector('[data-counter]');
    var caption = modal.querySelector('[data-caption]');
    var prev = modal.querySelector('[data-prev]');
    var next = modal.querySelector('[data-next]');

    if (!slides) return;
    var count = slides.children.length;
    slides.style.transform = 'translateX(-' + (state.currentIndex * 100) + '%)';
    if (counter) counter.textContent = (state.currentIndex + 1) + ' / ' + count;
    if (caption) {
      var slide = slides.children[state.currentIndex];
      caption.textContent = slide ? slide.getAttribute('data-caption') || '' : '';
    }
    if (prev) prev.disabled = state.currentIndex === 0;
    if (next) next.disabled = state.currentIndex >= count - 1;
  }
  function nextSlide() {
    var slides = modal.querySelector('[data-slides]');
    if (!slides) return;
    if (state.currentIndex < slides.children.length - 1) {
      state.currentIndex++;
      updateCarousel();
      track('project_modal_photo_viewed', {
        project_slug: state.currentSlug, index: state.currentIndex
      });
    }
  }
  function prevSlide() {
    if (state.currentIndex > 0) {
      state.currentIndex--;
      updateCarousel();
      track('project_modal_photo_viewed', {
        project_slug: state.currentSlug, index: state.currentIndex
      });
    }
  }

  /* ── Open / close ── */
  function open(slug) {
    var p = findProject(slug);
    if (!p || !modal) return;
    state.currentSlug = slug;
    state.openAt = Date.now();
    renderModal(p);
    if (typeof modal.showModal === 'function') {
      modal.showModal();
    } else {
      modal.setAttribute('open', '');
      modal.style.display = 'block';
    }
    if (location.hash !== '#project-' + slug) {
      history.pushState({ rsvProject: slug }, '', '#project-' + slug);
    }
    track('project_modal_opened', {
      project_slug: slug, project_type: p.project_type,
      style_label: p.style_label, color_label: p.color_label
    });
    // record galleries_viewed
    try {
      if (window.RSV_PROFILE) {
        var current = window.RSV_PROFILE.get('galleries_viewed') || [];
        current.push({ slug: slug, t: Date.now() });
        window.RSV_PROFILE.set('galleries_viewed', current.slice(-30));
      }
    } catch (e) {}
  }

  function close() {
    if (!modal) return;
    var slug = state.currentSlug;
    var dwell = state.openAt ? (Date.now() - state.openAt) : 0;
    if (typeof modal.close === 'function') {
      modal.close();
    } else {
      modal.removeAttribute('open');
      modal.style.display = '';
    }
    state.currentSlug = null;
    state.openAt = 0;
    if (location.hash.indexOf('#project-') === 0) {
      history.replaceState({}, '', location.pathname + location.search);
    }
    track('project_modal_closed', { project_slug: slug, dwell_ms: dwell });
  }

  function swap(slug) {
    var p = findProject(slug);
    if (!p) return;
    state.currentSlug = slug;
    state.openAt = Date.now();
    renderModal(p);
    history.replaceState({ rsvProject: slug }, '', '#project-' + slug);
    track('project_modal_opened', {
      project_slug: slug, project_type: p.project_type,
      style_label: p.style_label, color_label: p.color_label,
      swapped: true
    });
  }

  /* ── Tile binding (event delegation on document) ── */
  function bindTiles() {
    document.addEventListener('click', function (e) {
      var tile = e.target.closest('.rsv-pc[data-project-slug]');
      if (!tile) return;
      // ignore clicks on anchor children
      if (e.target.closest('a')) return;
      var slug = tile.getAttribute('data-project-slug');
      track('project_tile_clicked', {
        project_slug: slug,
        style_label: tile.getAttribute('data-style-label'),
        color_label: tile.getAttribute('data-color-label'),
        project_type: tile.getAttribute('data-project-type')
      });
      affinity('style_affinity', tile.getAttribute('data-style-label'), 0.3);
      affinity('color_affinity', tile.getAttribute('data-color-label'), 0.3);
      open(slug);
    });

    document.addEventListener('keydown', function (e) {
      if ((e.key === 'Enter' || e.key === ' ') && document.activeElement &&
          document.activeElement.matches && document.activeElement.matches('.rsv-pc[data-project-slug]')) {
        e.preventDefault();
        document.activeElement.click();
      }
    });
  }

  /* ── IntersectionObserver: tile impressions ── */
  function bindImpressions() {
    if (!('IntersectionObserver' in window)) return;
    var seen = new WeakSet();
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (ent) {
        if (ent.isIntersecting && ent.intersectionRatio >= 0.5 && !seen.has(ent.target)) {
          seen.add(ent.target);
          var el = ent.target;
          track('project_tile_impression', {
            project_slug: el.getAttribute('data-project-slug'),
            style_label: el.getAttribute('data-style-label'),
            color_label: el.getAttribute('data-color-label')
          });
        }
      });
    }, { threshold: [0.5] });

    function observeAll() {
      document.querySelectorAll('.rsv-pc[data-project-slug]').forEach(function (el) {
        io.observe(el);
      });
    }
    observeAll();
    // re-observe on dynamic content insertion (filter changes, pagination)
    var mo = new MutationObserver(observeAll);
    mo.observe(document.body, { childList: true, subtree: true });
  }

  /* ── Modal wiring ── */
  function bindModal() {
    modal = document.getElementById(MODAL_ID);
    if (!modal) return;

    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-close]')) close();
      if (e.target.closest('[data-prev]')) prevSlide();
      if (e.target.closest('[data-next]')) nextSlide();
    });

    modal.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') prevSlide();
      if (e.key === 'ArrowRight') nextSlide();
    });

    modal.addEventListener('close', function () {
      // native <dialog> close (Esc key). Fire tracking + clear hash.
      if (state.currentSlug) {
        var slug = state.currentSlug;
        var dwell = state.openAt ? (Date.now() - state.openAt) : 0;
        state.currentSlug = null;
        state.openAt = 0;
        if (location.hash.indexOf('#project-') === 0) {
          history.replaceState({}, '', location.pathname + location.search);
        }
        track('project_modal_closed', { project_slug: slug, dwell_ms: dwell });
      }
    });

    // Swipe support (mobile)
    var touchStartX = 0;
    modal.addEventListener('touchstart', function (e) { touchStartX = e.touches[0].clientX; }, { passive: true });
    modal.addEventListener('touchend', function (e) {
      var dx = e.changedTouches[0].clientX - touchStartX;
      if (Math.abs(dx) > 50) { if (dx < 0) nextSlide(); else prevSlide(); }
    }, { passive: true });
  }

  /* ── Deep link: open from URL hash on load + back/forward ── */
  function bindHashRouter() {
    function fromHash() {
      var m = /^#project-(.+)$/.exec(location.hash);
      if (m && m[1]) open(m[1]);
      else if (state.currentSlug) close();
    }
    window.addEventListener('popstate', fromHash);
    // run once after data loaded
    setTimeout(fromHash, 50);
  }

  /* ── Init ── */
  function init() {
    bindModal();
    bindTiles();
    bindImpressions();
    bindHashRouter();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.RSVProjectModal = { open: open, close: close, swap: swap };
})();
